import '../styles/pages/chat.scss'
import { ReactComponent as DoubleTickIndicator } from '../assets/double_tick_indicator.svg';
import { ReactComponent as ClockSVG } from "../assets/clock.svg";
import { ReactComponent as AirplaneIcon } from '../assets/airplane.svg';
import { ReactComponent as InfoIcon } from "../assets/info.svg";
import { ReactComponent as DocumentIcon } from "../assets/document.svg";
import { ReactComponent as PortfolioIcon } from "../assets/portfolio.svg";
import { ReactComponent as LocationIcon} from "../assets/location.svg";
import { ReactComponent as ReloadIcon } from "../assets/reload.svg";
import { ReactComponent as SwapIcon } from "../assets/swap.svg";
import { ReactComponent as HeadhunterIcon } from "../assets/hh.svg";



import {useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState} from "react";
import Loading from "../elements/Loading";
import moment from "moment";
import Popup from "../elements/popup";
import InlineNotification from "../elements/InlineNotification";
import getAPIClient from "../hooks/apiClient";

function getCaseOfYear(number) {
    if (5 <= (number % 100) && (number % 100) <= 20)
        return 'лет'
    else if (number % 10 === 1)
        return 'год'
    else if ([2, 3, 4].includes(number % 10))
        return 'года'
    else
        return 'лет'
}

function getCaseOfMonth(number) {
    if (5 <= (number % 100) && (number % 100) <= 20)
        return 'месяцев'
    else if (number % 10 === 1)
        return 'месяц'
    else if ([2, 3, 4].includes(number % 10))
        return 'месяца'
    else
        return 'месяцев'
}

const CHAT_BASE = '/services_interactions/hh/chat'

export default function ChatPage({BX24}) {
    const [isLoading, setIsLoading] = useState(false)
    const [globalNotificationMessage, setGlobalNotificationMessage] = useState('')
    const [globalNotificationType, setGlobalNotificationType] = useState('error')
    const [sendMessageErrorNotificationMessage, setSendMessageErrorNotificationMessage] = useState('')
    const [updateChatErrorNotificationMessage, setUpdateChatErrorNotificationMessage] = useState('')

    const [scrollChatFlag, setScrollChatFlag] = useState(false)
    useEffect(() => {
        lastChatElement.current?.scrollIntoView(true)
    }, [scrollChatFlag])

    const api = useMemo(() => getAPIClient(BX24), [])

    // --- HH-токены и helper для HH-запросов ---
    // hhTokens = {access_token, refresh_token, employer_id, employer_name} | null
    const [hhTokens, setHhTokens] = useState(null)
    const hhApi = useMemo(() => {
        if (!hhTokens) return null
        const instance = getAPIClient(BX24)
        instance.defaults.headers['X-HH-ACCESS-TOKEN'] = hhTokens.access_token
        return instance
    }, [hhTokens])

    // --- OAuth: clientID и flow ---
    const HHRedirectURL = useMemo(() => `${window.location.origin}/hh_chat_authorize`, [])
    const [HHClientID, setHHClientID] = useState(undefined)
    useEffect(() => {
        api.get('/services_interactions/hh/app_authorization_data').then((r) => {
            setHHClientID(r.data.hh_client_id)
        }).catch(() => {
            setGlobalNotificationMessage('Произошла ошибка при попытке получить данные для авторизации. Пожалуйста, напишите в ТП')
            setGlobalNotificationType('error')
        })
    }, [api])

    // Получаем OAuth-код из попапа.
    // Принимаем только структурированное сообщение {type: 'hh_oauth_code', code: '...'}.
    // Это отсеивает посторонние same-origin postMessage'и (Yandex Metrika с __ym__pr,
    // Bitrix SDK и т.п.), которые иначе случайно интерпретировались как OAuth-код.
    const [authorizationCode, setAuthorizationCode] = useState()
    useEffect(() => {
        if (hhTokens) return
        function receiveMessage(event) {
            if (event.origin !== window.location.origin) return
            const data = event.data
            if (!data || typeof data !== 'object' || data.type !== 'hh_oauth_code') return
            if (typeof data.code !== 'string' || !data.code) return
            setAuthorizationCode(data.code)
        }
        window.addEventListener("message", receiveMessage, false);
        return () => window.removeEventListener("message", receiveMessage, false)
    }, [hhTokens])

    const auth = useCallback(() => {
        if (!HHClientID) return
        // Сбрасываем накопившуюся ошибку (например после неудачного предыдущего exchange)
        setGlobalNotificationMessage('')
        const url = 'https://hh.ru/oauth/authorize?' +
            `response_type=code&` +
            `client_id=${encodeURIComponent(HHClientID)}&` +
            `redirect_uri=${encodeURIComponent(HHRedirectURL)}`
        window.open(url, `hh_chat_auth_${Date.now()}`, 'popup')
    }, [HHClientID, HHRedirectURL])

    // Обмен OAuth-кода на токены (один раз на код).
    // Защита: hhTokens уже есть → пропускаем; код уже обменивали → пропускаем.
    const exchangedCodesRef = useRef(new Set())
    useEffect(() => {
        if (!authorizationCode || hhTokens) return
        const code = authorizationCode
        // Сразу сбрасываем, чтобы при ре-фаере effect'а не отправить тот же одноразовый код
        setAuthorizationCode(undefined)
        if (exchangedCodesRef.current.has(code)) return
        exchangedCodesRef.current.add(code)
        setIsLoading(true)
        api.post(`${CHAT_BASE}/oauth/exchange`, {
            authorization_code: code,
            redirect_url: HHRedirectURL,
        }).then((r) => {
            setHhTokens(r.data)
            setGlobalNotificationMessage('')
        }).catch((e) => {
            setIsLoading(false)
            // 400 — обычно "code not found" из-за повторной отправки уже использованного кода.
            // Не показываем юзеру страшную ошибку, просто оставляем экран авторизации, юзер кликнет ещё раз.
            if (e?.response?.status === 400) {
                console.warn('OAuth exchange 400 ignored:', e?.response?.data)
                return
            }
            const msg = e?.response?.data?.detail || 'Ошибка авторизации hh.ru'
            setGlobalNotificationType('error')
            setGlobalNotificationMessage(msg)
        })
    }, [authorizationCode, api, HHRedirectURL, hhTokens])

    // --- B24 element_id из placement ---
    const B24ElementID = useMemo(() => {
        const placement = BX24.placement.info();
        return placement?.options?.ID ? `${placement.options.ID}` : `${placement.options.ENTITY_ID}`
    }, [])

    // --- Резюме ---
    const [resume, setResume] = useState()
    useEffect(() => {
        if (!hhApi || !B24ElementID) return
        setIsLoading(true)
        hhApi.get(`${CHAT_BASE}/resume`, {params: {b24_element_id: B24ElementID}}).then((r) => {
            setResume(r.data)
        }).catch((e) => {
            setIsLoading(false)
            const msg = e?.response?.data?.detail || 'Не удалось получить резюме'
            setGlobalNotificationType('error')
            setGlobalNotificationMessage(msg)
        })
    }, [hhApi, B24ElementID])

    // --- Вакансии ---
    const [availableVacancies, setAvailableVacancies] = useState()
    const [popupVacanciesSelectorOpenedFlag, setPopupVacanciesSelectorOpenedFlag] = useState(false)

    const loadVacancies = useCallback(() => {
        if (!hhApi || !resume || !hhTokens) return
        setIsLoading(true)
        hhApi.get(`${CHAT_BASE}/vacancies`, {params: {resume_id: resume.id, employer_id: hhTokens.employer_id}}).then((r) => {
            setAvailableVacancies(r.data)
            setPopupVacanciesSelectorOpenedFlag(true)
            setIsLoading(false)
        }).catch((e) => {
            setIsLoading(false)
            const msg = e?.response?.data?.detail || 'Не удалось получить список вакансий'
            setGlobalNotificationType('error')
            setGlobalNotificationMessage(msg)
        })
    }, [hhApi, resume, hhTokens])

    // Автоматическая загрузка вакансий после получения резюме
    useEffect(() => {
        if (hhApi && resume) {
            loadVacancies()
        }
    }, [hhApi, resume])

    const changeVacancy = useCallback(() => {
        loadVacancies()
    }, [loadVacancies])

    // --- Выбор вакансии + поиск отклика/приглашения ---
    const [selectedVacancy, setSelectedVacancy] = useState()
    const [negotiationFoundFlag, setNegotiationFoundFlag] = useState()
    const [negotiationId, setNegotiationId] = useState()

    const selectVacancy = useCallback((vacancy_id) => {
        if (!hhApi || !resume || !hhTokens) return
        setIsLoading(true)
        setNegotiationFoundFlag(undefined)
        setSelectedVacancy(undefined)
        setNegotiationId(undefined)
        setChatMessages([])
        hhApi.post(`${CHAT_BASE}/vacancy/select`, {
            vacancy_id: String(vacancy_id),
            resume_id: resume.id,
            employer_id: hhTokens.employer_id,
        }).then((r) => {
            setSelectedVacancy(r.data.vacancy)
            setNegotiationFoundFlag(!!r.data.negotiation_found)
            setNegotiationId(r.data.negotiation_id || undefined)
            setPopupVacanciesSelectorOpenedFlag(false)
            setIsLoading(false)
        }).catch((e) => {
            setIsLoading(false)
            const msg = e?.response?.data?.detail || 'Не удалось выбрать вакансию'
            setGlobalNotificationType('error')
            setGlobalNotificationMessage(msg)
        })
    }, [hhApi, resume, hhTokens])

    // --- Отправка приглашения ---
    const sendNegotiation = useCallback(() => {
        if (!hhApi || !selectedVacancy || !resume || !hhTokens) return
        if (!selectedVacancy.negotiations_actions || selectedVacancy.negotiations_actions.length === 0) return
        setIsLoading(true)
        hhApi.post(`${CHAT_BASE}/negotiation/invite`, {
            vacancy_id: String(selectedVacancy.id),
            resume_id: resume.id,
            employer_id: hhTokens.employer_id,
        }).then((r) => {
            setSelectedVacancy(r.data.vacancy)
            setNegotiationFoundFlag(true)
            setNegotiationId(r.data.negotiation_id || undefined)
            setIsLoading(false)
        }).catch((e) => {
            setIsLoading(false)
            const msg = e?.response?.data?.detail || 'Не удалось отправить приглашение'
            setGlobalNotificationType('error')
            setGlobalNotificationMessage(msg)
        })
    }, [hhApi, selectedVacancy, resume, hhTokens])

    // --- Чат: получение и автообновление ---
    const [chatMessages, setChatMessages] = useState([])

    // Первая загрузка чата полностью когда есть negotiation_id
    useEffect(() => {
        if (!hhApi || !negotiationFoundFlag || !negotiationId) return
        setIsLoading(true)
        hhApi.get(`${CHAT_BASE}/negotiation/${negotiationId}/messages`, {params: {full: true}}).then((r) => {
            setChatMessages(r.data)
            setIsLoading(false)
            setScrollChatFlag(prev => !prev)
        }).catch((e) => {
            setIsLoading(false)
            const msg = e?.response?.data?.detail || 'Не удалось получить чат'
            setUpdateChatErrorNotificationMessage(`${msg} [${moment().format('DD.MM HH:mm')}]`)
        })
    }, [hhApi, negotiationFoundFlag, negotiationId])

    const handleMessagesUpdate = useCallback((messages) => {
        setUpdateChatErrorNotificationMessage('')
        let copiedChatMessages = JSON.parse(JSON.stringify(chatMessages))
        let currentChatIDs = copiedChatMessages.map((m) => m.id)
        let newMessagesFoundFlag = false
        let newMessageFromApplicantFoundFlag = false
        for (let i in messages) {
            let message = messages[i]
            if (!currentChatIDs.includes(message.id)) {
                copiedChatMessages.push(message)
                newMessagesFoundFlag = true
                if (message.author?.participant_type === 'applicant') {
                    newMessageFromApplicantFoundFlag = true
                }
            } else {
                for (let j in copiedChatMessages) {
                    if (message.id === copiedChatMessages[j].id) {
                        copiedChatMessages[j] = message
                    }
                }
            }
        }
        let updatedChatMessages = []
        for (let i in copiedChatMessages) {
            let message = copiedChatMessages[i]
            if (newMessagesFoundFlag && message.is_pending) {}
            else {
                updatedChatMessages.push(message)
            }
        }
        updatedChatMessages = updatedChatMessages.sort((a, b) => a - b)
        setChatMessages(updatedChatMessages)
        if (newMessageFromApplicantFoundFlag) {
            setScrollChatFlag(prev => !prev)
        }
    }, [chatMessages, scrollChatFlag])

    // Поллинг новых сообщений каждые 3с.
    // После MAX_POLL_FAILURES подряд (~истёкший BX24-токен и т.п.) — стопаем интервал,
    // чтобы не засирать сервер 1200 req/час и не спамить юзеру ошибкой каждые 3с.
    // Юзеру показываем баннер с предложением переподключиться (reload вкладки).
    const MAX_POLL_FAILURES = 3
    const pollFailuresRef = useRef(0)
    const [pollDeadFlag, setPollDeadFlag] = useState(false)
    useEffect(() => {
        if (!hhApi || !negotiationFoundFlag || !negotiationId) return
        if (pollDeadFlag) return
        pollFailuresRef.current = 0
        const id = setInterval(() => {
            hhApi.get(`${CHAT_BASE}/negotiation/${negotiationId}/messages`, {params: {full: false}}).then((r) => {
                pollFailuresRef.current = 0
                handleMessagesUpdate(r.data)
            }).catch((e) => {
                pollFailuresRef.current += 1
                const msg = e?.response?.data?.detail || 'Не удалось обновить чат'
                setUpdateChatErrorNotificationMessage(`${msg} [${moment().format('DD.MM HH:mm')}]`)
                if (pollFailuresRef.current >= MAX_POLL_FAILURES) {
                    clearInterval(id)
                    setPollDeadFlag(true)
                    setUpdateChatErrorNotificationMessage(
                        `Соединение потеряно (${MAX_POLL_FAILURES} ошибки подряд). Нажмите «Переподключиться», чтобы продолжить.`
                    )
                }
            })
        }, 3000)
        return () => clearInterval(id)
    }, [hhApi, negotiationFoundFlag, negotiationId, handleMessagesUpdate, pollDeadFlag])

    const reconnectChat = useCallback(() => {
        try {
            if (typeof BX24?.refreshAuth === 'function') {
                BX24.refreshAuth(() => window.location.reload())
                return
            }
        } catch (_) {}
        window.location.reload()
    }, [BX24])

    // --- Отправка сообщения ---
    const [newMessageValue, setNewMessageValue] = useState("");
    const sendChatMessage = useCallback(() => {
        const text = newMessageValue.trim()
        if (!text || !hhApi || !negotiationId) return
        setSendMessageErrorNotificationMessage('')
        // оптимистично добавляем сообщение
        setChatMessages(prevState => [...prevState, {
            "id": (prevState[prevState.length - 1]?.id ?? 0) + 1,
            "text": text,
            "state": {"id": "text", "name": "Текст"},
            "author": {"participant_type": "employer"},
            "is_pending": true
        }])
        setNewMessageValue('')
        setScrollChatFlag(prev => !prev)
        hhApi.post(`${CHAT_BASE}/negotiation/${negotiationId}/messages`, {text}).catch((e) => {
            const msg = e?.response?.data?.detail || 'Не удалось отправить сообщение'
            setSendMessageErrorNotificationMessage(`${msg} [${moment().format('DD.MM HH:mm')}]`)
            setScrollChatFlag(prev => !prev)
        })
    }, [hhApi, negotiationId, newMessageValue])

    // --- Комментарии соискателя ---
    const [applicantComments, setApplicantComments] = useState([])
    const [commentsExpanded, setCommentsExpanded] = useState(false)
    const [newCommentText, setNewCommentText] = useState('')
    const [commentsError, setCommentsError] = useState('')
    const applicantId = useMemo(() => (resume?.owner?.id ? String(resume.owner.id) : null), [resume])

    const loadComments = useCallback(() => {
        if (!hhApi || !applicantId) return
        hhApi.get(`${CHAT_BASE}/applicant_comments`, {params: {applicant_id: applicantId}}).then((r) => {
            // Бэк возвращает массив items напрямую (см. get_applicant_comments в api_client)
            setApplicantComments(Array.isArray(r.data) ? r.data : (r.data?.items || []))
            setCommentsError('')
        }).catch((e) => {
            const msg = e?.response?.data?.detail || 'Не удалось загрузить комментарии'
            setCommentsError(msg)
        })
    }, [hhApi, applicantId])

    useEffect(() => {
        loadComments()
    }, [loadComments])

    const sendApplicantComment = useCallback(() => {
        const trimmed = newCommentText.trim()
        if (!trimmed || !hhApi || !applicantId) return
        setCommentsError('')
        hhApi.post(`${CHAT_BASE}/applicant_comments`, {applicant_id: applicantId, text: trimmed}).then(() => {
            setNewCommentText('')
            loadComments()
        }).catch((e) => {
            const msg = e?.response?.data?.detail || 'Не удалось отправить комментарий'
            setCommentsError(msg)
        })
    }, [hhApi, applicantId, newCommentText, loadComments])

    // --- Render ---
    const resumeInfoList = useMemo(() => {
        let negotiationType = 'Отклик или приглашение не найдены'
        if (negotiationFoundFlag) {
            negotiationType = selectedVacancy?.negotiations_state?.name || negotiationType
        }
        let experience;
        if (resume) {
            let months = resume.total_experience ? resume.total_experience.months : 0
            experience = `${months % 12} ${getCaseOfMonth(months % 12)}`
            let years = Math.floor(months / 12)
            if (years >= 1)
                experience = `${years} ${getCaseOfYear(years)} ${experience}`
        }

        return [{
            icon: <DocumentIcon/>,
            name: 'Вакансия',
            value: `${selectedVacancy?.name ? selectedVacancy.name : 'Вакансия не выбрана'}`
        },{
            icon: <InfoIcon/>,
            name: 'Отклик или приглашение',
            value: negotiationType
        },{
            icon: <PortfolioIcon/>,
            name: 'Опыт работы',
            value: resume ? experience : 'Неизвестно'
        },{
            icon: <LocationIcon/>,
            name: 'Локация соискателя',
            value: resume?.area?.name ? resume.area.name : 'Не указано'
        }]
    }, [negotiationFoundFlag, selectedVacancy, resume])

    const MIN_TEXTAREA_HEIGHT = 20;
    const textareaRef = useRef(null);
    const chatRef = useRef()
    const lastChatElement = useRef()

    useEffect(() => {
        textareaRef.current?.focus()
    }, [])

    useLayoutEffect(() => {
        if (!textareaRef.current || !chatRef.current) return
        textareaRef.current.style.height = "inherit";
        const heightOfTextarea = Math.max(
            textareaRef.current.scrollHeight,
            MIN_TEXTAREA_HEIGHT
        )
        textareaRef.current.style.height = `${heightOfTextarea}px`;
        chatRef.current.style.height = `calc(100% - 20px - 5px - 1px - ${heightOfTextarea}px)`
    }, [newMessageValue]);

    const handleKeyPressTextarea = useCallback((event) => {
        if (event.key === 'Enter' && !event.shiftKey){
            sendChatMessage()
            event.preventDefault()
        }
    }, [sendChatMessage])

    const HHAuthorizedFlag = !!hhTokens

    return <div className={'chatpage-main-block'}>
        {(globalNotificationMessage) && <div className={'global-error-main'}>
            <div className={'global-error-block'}>
                <InlineNotification text={globalNotificationMessage} type={globalNotificationType} hideFlag={false}/>
                <button className={'global-error-reload-button'} onClick={() => window.location.reload()}>
                    <ReloadIcon/>
                </button>
            </div>
        </div>}
        {(!HHAuthorizedFlag) && <div className={'global-error-main'} style={{top: globalNotificationMessage ? '50%' : undefined}}>
            <div className={'global-error-block'}>
                <button className={'global-error-reload-button'} onClick={auth}>
                    Авторизоваться на hh.ru
                </button>
            </div>
        </div>
        }
        <Loading isLoading={isLoading}/>
        <Popup openedFlag={popupVacanciesSelectorOpenedFlag} canBeClosedFlag={false}>
            <div className={'vacancies-selector-header'}>Выберите вакансию, по которой хотите вести общение</div>
            <div className={'vacancy-list'}>
                {(!!availableVacancies?.length) ? availableVacancies.map(el => <div className={'vacancy'} key={el.id} onClick={() => selectVacancy(el.id)}>
                    <div className={'vacancy-name'}>
                        {el.name}
                    </div>
                    <div className={'vacancy-city'}>
                        {el.area?.name}
                    </div>
                </div>) : <></>}
                {(!availableVacancies?.length) && <div className={'vacancies-not-found'}>
                Доступных вакансий не найдено
                </div>}
            </div>
        </Popup>
        <div className={'chat-block'}>
            <div className={'chat-body'} ref={chatRef}>
                {negotiationFoundFlag === false &&
                    <div className={'chat-center-container'}>
                        <div className={'chat-clear-message'}>
                            <div className={'negotiation-not-found-text'}>Отклик или приглашение не найдены</div>
                            {(!!selectedVacancy?.negotiations_actions?.length) && <button className={'negotiation-send-button'}
                                     onClick={sendNegotiation}>Пригласить</button>}
                        </div>
                    </div>}
                {(chatMessages.length === 0 && negotiationFoundFlag) &&
                    <div className={'chat-center-container'}>
                        <div className={'chat-clear-message'}>
                            Сообщений не найдено
                        </div>
                    </div>
                }
                {chatMessages.map(message =>
                    <div className={'chat-row'} key={message.id}>
                        <div className={`message ${message.author?.participant_type === 'applicant' ? 'message-left' : 'message-right' }`}>
                            <div className={`message-text ${(!message.text) ? "message-not-allowed" : ""}`}>{message.text ? message.text : 'Тип сообщения не поддерживается'}</div>
                            <div className={'message-info'}>
                                {(message.author?.participant_type !== 'applicant') &&
                                    <div className={`message-read-status ${message.viewed_by_opponent ? 'status-read' : ''}`}>
                                        {(!message?.is_pending) ?
                                            <DoubleTickIndicator/>
                                            : <ClockSVG/>
                                        }
                                    </div>
                                }
                                <div className={'message-write-time'}>
                                    {moment(message.created_at).format('DD.MM HH:mm')}
                                </div>
                            </div>
                        </div>
                    </div>
                )}
                <div ref={lastChatElement} className={'last-chat-element'}></div>
                {!!updateChatErrorNotificationMessage &&
                    <InlineNotification
                        className={'local-chat-error'}
                        text={updateChatErrorNotificationMessage}
                        type={'error'}
                        hideFlag={false}
                    />
                }
                {pollDeadFlag &&
                    <button
                        type={'button'}
                        className={'reconnect-chat-button'}
                        onClick={reconnectChat}
                    >
                        Переподключиться
                    </button>
                }
                {!!sendMessageErrorNotificationMessage &&
                    <InlineNotification
                        className={'local-chat-error'}
                        text={sendMessageErrorNotificationMessage}
                        type={'error'}
                        hideFlag={false}
                    />
                }
            </div>
            <div className={'chat-input'}>
                <textarea
                    disabled={negotiationFoundFlag === false || negotiationFoundFlag === undefined}
                    contentEditable={true}
                    placeholder={(negotiationFoundFlag !== false && negotiationFoundFlag !== undefined) ? 'Написать сообщение...' : 'Чат с соискателем не доступен'}
                    rows={1}
                    onChange={(event) => setNewMessageValue(event.target.value)}
                    ref={textareaRef}
                    style={{
                        minHeight: MIN_TEXTAREA_HEIGHT,
                        resize: "none"
                    }}
                    onKeyDown={handleKeyPressTextarea}
                    value={newMessageValue}
                />
                <button disabled={!newMessageValue.trim()} onClick={sendChatMessage}>
                    <AirplaneIcon/>
                </button>
            </div>
        </div>
        <div className={'profile-info-block'}>
            <div className={'profile-main-info'}>
                <div className={'profile-photo'}>
                    <img className={'profile-photo-img'} alt={'profile'} src={
                        resume?.photo?.medium ? resume?.photo?.medium : 'https://icon-library.com/images/flat-person-icon/flat-person-icon-23.jpg'
                    }/>
                </div>
                <div className={'profile-fio-and-resume'}>
                    <div className={'profile-fio'}>
                        {resume?.first_name ?
                            `${resume.last_name} ${resume.first_name}${resume?.middle_name ? " " + resume.middle_name : ""}`
                            : 'Соискатель'
                        }
                    </div>
                    <div className={'profile-resume'}>
                        {resume?.title ? resume.title : 'Должность не указана'}
                    </div>
                </div>
            </div>
            <div className={'info-blocks'}>
                {resumeInfoList.map((el, idx) =>
                    <div className={'info-block'} key={idx}>
                        <div className={'info-block-icon'}>{el.icon}</div>
                        <div className={'info-block-data'}>
                            <div className={'info-block-value'}>
                                {el.value}
                            </div>
                            <div className={'info-block-name'}>
                                {el.name}
                            </div>
                        </div>
                    </div>
                )}
                <div className={`info-block info-block-comments ${commentsExpanded ? 'expanded' : ''}`}>
                    <div className={'comments-row'} onClick={() => setCommentsExpanded(p => !p)}>
                        <div className={'info-block-icon'}>
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                            </svg>
                        </div>
                        <div className={'info-block-data'}>
                            <div className={'info-block-value'}>
                                Комментарии{applicantComments?.length > 0 ? ` (${applicantComments.length})` : ''}
                            </div>
                            <div className={'info-block-name'}>
                                История заметок с hh.ru
                            </div>
                        </div>
                        <div className={'comments-toggle-wrap'}>
                            <span className={`comments-toggle ${commentsExpanded ? 'open' : ''}`}>▸</span>
                        </div>
                    </div>
                    <div className={'comments-body'}>
                        <div className={'comments-body-inner'}>
                            {commentsError && <InlineNotification type={'error'} text={commentsError} hideFlag={false}/>}
                            <div className={'comments-list'}>
                                {applicantComments?.length === 0 && <div className={'comments-empty'}>Комментариев пока нет</div>}
                                {applicantComments?.map(c => (
                                    <div key={c.id} className={'comment-item'}>
                                        <div className={'comment-meta'}>
                                            {(c.author?.full_name || '—')} · {c.created_at ? moment(c.created_at).format('DD.MM.YYYY HH:mm') : ''}
                                        </div>
                                        <div className={'comment-text'}>{c.text}</div>
                                    </div>
                                ))}
                            </div>
                            <textarea
                                className={'comment-input'}
                                value={newCommentText}
                                onChange={e => setNewCommentText(e.target.value)}
                                placeholder="Новый комментарий…"
                                rows={2}
                            />
                            <button
                                className={'comment-submit'}
                                onClick={sendApplicantComment}
                                disabled={!newCommentText.trim()}
                            >
                                Добавить
                            </button>
                        </div>
                    </div>
                </div>
            </div>
            <div className={'actions-blocks'}>
                <button className={'select-vacancy-button'} onClick={changeVacancy}>
                    <div className={'action-block-icon'}><SwapIcon/></div>
                    <div className={'action-block-name'}>Сменить вакансию</div>
                </button>
                <button className={'open-hh-button'} onClick={() => window.open(
                    `https://hh.ru/resume/${resume?.id}`,'_blank'
                )}>
                    <div className={'action-block-icon'}><HeadhunterIcon/></div>
                    <div className={'action-block-name'}>Открыть на Headhunter</div>
                </button>
            </div>
        </div>
    </div>
}
